import React from "react"
import { FormTextField, FormAutocomplete } from "@carrotfertility/carotene-core-x"
import { useIntl } from "react-intl"
import { UsaStates } from "#/utils/UsaStateOptions"
import { AboutYou } from "#/types/carrotPlanTypes"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { useIntlSort } from "#/utils/hooks/useIntlSort"

export type AddressFields = Omit<AboutYou["address"], "internationalCountry" | "internationalAddressLine" | "state"> & {
  state: { label: string; value: string }
}

interface AddressProps {
  defaultValues: Partial<Omit<AboutYou["address"], "internationalCountry" | "internationalAddressLine">>
  validatePoBox?: boolean
}

export function formatAddressFieldsToPayload(fields: AddressFields) {
  return {
    address: {
      address1: fields?.address1,
      address2: fields?.address2,
      city: fields?.city,
      state: fields?.state?.value,
      zip: fields?.zip
    }
  }
}

/**
 * Detects if an address string contains a PO box pattern
 * Matches common variations like "P.O. Box", "PO Box", "Post Office Box", "POB", etc.
 * Uses word boundaries to match PO box patterns anywhere in the address string.
 */
function isPoBoxAddress(address: string): boolean {
  if (!address) return false
  const normalized = address.trim().toUpperCase()
  // Match patterns like:
  // - P.O. Box, PO Box, P.O Box, PO. Box, P.O. box, P.O.box (with or without numbers)
  // - Post Office Box (with or without numbers)
  // - POB, POBOX (with or without numbers)
  // - Box 123 (when Box is followed by a number)
  // Using word boundaries (\b) to match anywhere in the string, not just at the start
  // This catches cases like "123 PO Box" or "123 P.O. Box"
  const poBoxPattern =
    /\b(P\.?O\.?\s*BOX(\s+\d+)?)\b|\b(P\.?O\.?\s*B\.?(\s+\d+)?)\b|\b(POST\s+OFFICE\s+BOX(\s+\d+)?)\b|\b(POB|POBOX)(\s+\d+)?\b|\b(BOX\s+\d+)\b/i
  return poBoxPattern.test(normalized)
}

export function Address(props: AddressProps) {
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const theme = useTheme()
  const requiredText = intl.formatMessage({ defaultMessage: "Required" })
  const stateOptions: { label: string; value: string }[] = React.useMemo(
    () =>
      UsaStates.map((state) => ({
        label: intl.formatMessage(state.label),
        value: state.id
      })).sort((a, b) => localeComparator(a.label, b.label)),
    [intl, localeComparator]
  )
  const poBoxErrorText = intl.formatMessage({ defaultMessage: "Street address cannot be a PO box" })
  const address1HelperText = props.validatePoBox
    ? intl.formatMessage({
        defaultMessage: "Include apartment, suite, unit, building, floor, etc. This address cannot be a PO box."
      })
    : undefined

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <FormTextField
        name={"address1"}
        label={intl.formatMessage({ defaultMessage: "Address Line 1" })}
        helperText={address1HelperText}
        registerOptions={{
          required: requiredText,
          validate: props.validatePoBox
            ? (value) => {
                if (!value) {
                  return requiredText
                }
                if (isPoBoxAddress(value)) {
                  return poBoxErrorText
                }
                return true
              }
            : undefined
        }}
        inputProps={{ "data-testid": "address1" }}
        defaultValue={props?.defaultValues?.address1}
      />
      <FormTextField
        name={"address2"}
        label={intl.formatMessage({ defaultMessage: "Address Line 2" })}
        inputProps={{ "data-testid": "address2" }}
        defaultValue={props?.defaultValues?.address2}
      />
      <FormTextField
        name={"city"}
        label={intl.formatMessage({ defaultMessage: "City" })}
        inputProps={{ "data-testid": "city" }}
        registerOptions={{ required: requiredText }}
        defaultValue={props?.defaultValues?.city}
      />
      <FormAutocomplete
        name="state"
        fullWidth
        label={intl.formatMessage({ defaultMessage: "State" })}
        data-testid="state"
        options={stateOptions}
        controllerProps={{
          defaultValue: stateOptions.find(({ value }) => value === props?.defaultValues?.state),
          rules: { required: requiredText }
        }}
      />
      <FormTextField
        name={"zip"}
        label={intl.formatMessage({ defaultMessage: "Zip Code" })}
        inputProps={{ "data-testid": "zip" }}
        registerOptions={{
          required: requiredText,
          validate: (value) => {
            if (!value) {
              return requiredText
            }
            if (!value.match(/^\d{5}$/)) {
              return intl.formatMessage({ defaultMessage: "Zip code must be 5 digits long" })
            }
            return true
          }
        }}
        defaultValue={props?.defaultValues?.zip}
      />
    </Stack>
  )
}
